import { differenceInCalendarDays, format } from "date-fns";
import { CrmTaskStatus, type CrmTask } from "@/types/crm";

export type TaskGroupKey = "overdue" | "today" | "later";

export interface TaskGroup {
  key: TaskGroupKey;
  label: string;
  tasks: CrmTask[];
}

const LABELS: Record<TaskGroupKey, string> = {
  overdue: "Overdue",
  today: "Today",
  later: "Later",
};

function dueTime(task: CrmTask) {
  return task.dueDate ? new Date(task.dueDate).getTime() : Number.POSITIVE_INFINITY;
}

export interface GroupTasksOptions {
  /** Keep completed tasks in their due-date bucket (dashboard "just completed"). */
  keepCompleted?: boolean;
}

export function groupTasks(
  tasks: CrmTask[],
  now: Date = new Date(),
  options: GroupTasksOptions = {},
): TaskGroup[] {
  const buckets: Record<TaskGroupKey, CrmTask[]> = { overdue: [], today: [], later: [] };

  for (const task of tasks) {
    if (task.status === CrmTaskStatus.Completed && !options.keepCompleted) continue;
    if (!task.dueDate) {
      buckets.later.push(task);
      continue;
    }
    const days = differenceInCalendarDays(new Date(task.dueDate), now);
    if (days < 0) buckets.overdue.push(task);
    else if (days === 0) buckets.today.push(task);
    else buckets.later.push(task);
  }

  (Object.keys(buckets) as TaskGroupKey[]).forEach((key) => {
    buckets[key].sort((a, b) => dueTime(a) - dueTime(b));
  });

  return (Object.keys(buckets) as TaskGroupKey[])
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({ key, label: LABELS[key], tasks: buckets[key] }));
}

export function dueChipLabel(task: CrmTask, now: Date = new Date()): string {
  if (!task.dueDate) return "No due date";
  const due = new Date(task.dueDate);
  const days = differenceInCalendarDays(due, now);
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} late`;
  // `days` is computed against the caller's `now` — using date-fns' isToday()
  // here would consult the system clock and ignore the injected reference time.
  if (days === 0) return `Today ${format(due, "HH:mm")}`;
  return format(due, "EEE");
}
